import React, { useState } from 'react';
import { useRole } from '@xanectech/auth';
import { AppRole } from '@xanectech/types';
import { supabase, type Insertable } from '@xanectech/supabase-client';
import { Button } from '@xanectech/ui/components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xanectech/ui/components';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@xanectech/ui/components';
import { Badge } from '@xanectech/ui/components';
import { useToast } from '@xanectech/ui/lib';
import { Shield, UserCheck, AlertTriangle } from 'lucide-react';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - SecureRoleManager');

interface SecureRoleManagerProps {
  targetUserId: string;
  targetUserEmail: string;
  companyId?: string;
  onRoleUpdated?: () => void;
}

export const SecureRoleManager: React.FC<SecureRoleManagerProps> = ({
  targetUserId,
  targetUserEmail,
  companyId,
  onRoleUpdated,
}) => {
  const { currentRole, hasPermission } = useRole();
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState<AppRole>('end_user');
  const [isAssigning, setIsAssigning] = useState(false);

  // Define role assignment authorization rules
  const getAvailableRoles = (): AppRole[] => {
    if (currentRole === 'super_admin') {
      return ['super_admin', 'internal_admin', 'company_admin', 'end_user'];
    }
    if (currentRole === 'internal_admin') {
      return ['company_admin', 'end_user'];
    }
    if (currentRole === 'company_admin') {
      return ['end_user'];
    }
    return [];
  };

  const handleRoleAssignment = async () => {
    if (!hasPermission('canManageUsers')) {
      toast({
        title: 'Access Denied',
        description: "You don't have permission to assign roles.",
        variant: 'destructive',
      });
      return;
    }

    setIsAssigning(true);
    try {
      const assignerId = (await supabase.auth.getUser()).data.user?.id;
      if (!assignerId) {
        throw new Error('Unable to identify assigner');
      }

      // First, delete any existing roles for this user in the same context (company or system-wide)
      const deleteQuery = supabase
        .from('user_roles')
        .delete()
        .eq('user_id', targetUserId);

      if (companyId && companyId.trim() !== '') {
        deleteQuery.eq('company_id', companyId);
      } else {
        deleteQuery.is('company_id', null);
      }

      const { error: deleteError } = await deleteQuery;

      if (deleteError) {
        logger.error('Role cleanup error:', deleteError);
        toast({
          title: 'Assignment Failed',
          description: 'Failed to update role. Please try again.',
          variant: 'destructive',
        });
        setIsAssigning(false);
        return;
      }

      // Now insert the new role
      const payload: Insertable<'user_roles'> = {
        user_id: targetUserId,
        role: selectedRole,
        is_active: true,
        assigned_by: assignerId,
        company_id: null,
      };

      if (companyId && companyId.trim() !== '') {
        payload.company_id = companyId;
      }

      const { error } = await supabase.from('user_roles').insert(payload);

      if (error) {
        logger.error('Role assignment error:', error);
        toast({
          title: 'Assignment Failed',
          description: error.message.includes('authorize_role_assignment')
            ? "You're not authorized to assign this role."
            : 'Failed to assign role. Please try again.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Role Assigned',
          description: `Successfully assigned ${selectedRole.replace('_', ' ')} role to ${targetUserEmail}.`,
        });
        onRoleUpdated?.();
      }
    } catch (error) {
      logger.error('Unexpected error:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
    setIsAssigning(false);
  };

  const availableRoles = getAvailableRoles();

  if (availableRoles.length === 0) {
    return (
      <Card className='w-full max-w-md'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-warning' />
            Access Restricted
          </CardTitle>
          <CardDescription>
            You don't have permission to assign roles to other users.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className='w-full max-w-md'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Shield className='h-5 w-5 text-primary' />
          Assign Role
        </CardTitle>
        <CardDescription>Assign a role to {targetUserEmail}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='space-y-2'>
          <label className='text-sm font-medium'>Role</label>
          <Select
            value={selectedRole}
            onValueChange={(value) => setSelectedRole(value as AppRole)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select a role' />
            </SelectTrigger>
            <SelectContent>
              {availableRoles.map((role) => (
                <SelectItem key={role} value={role}>
                  <div className='flex items-center gap-2'>
                    <UserCheck className='h-4 w-4' />
                    <span className='capitalize'>{role.replace('_', ' ')}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-2'>
          <label className='text-sm font-medium'>Your Current Role</label>
          <Badge variant='secondary' className='capitalize'>
            {currentRole?.replace('_', ' ')}
          </Badge>
        </div>

        <Button
          onClick={handleRoleAssignment}
          disabled={isAssigning}
          className='w-full'
        >
          {isAssigning ? 'Assigning...' : 'Assign Role'}
        </Button>
      </CardContent>
    </Card>
  );
};
